import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs';

import { APP_ENV } from '../../../core/tokens/app-environment.token';
import type {
  WorkspaceFilePublicDto,
  WorkspaceFilesIngestionStatusResponseDto,
} from '../opportunity.mapper';

export type {
  WorkspaceFilePublicDto,
  WorkspaceFilesIngestionStatusResponseDto,
} from '../opportunity.mapper';

/** HTTP only — workspace file list, upload, delete, and ingestion status. */
@Injectable({ providedIn: 'root' })
export class OpportunityWorkspaceApiService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(APP_ENV);

  uploadWorkspaceFiles(workspaceId: string, files: File[]) {
    return this.postWorkspaceFiles(workspaceId, 'customer-documents', files);
  }

  uploadInternalDocuments(workspaceId: string, files: File[]) {
    return this.postWorkspaceFiles(workspaceId, 'internal-documents', files);
  }

  private postWorkspaceFiles(workspaceId: string, route: string, files: File[]) {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file, file.name);
    }
    return this.http.post<WorkspaceFilePublicDto[]>(
      `${this.env.apiBaseUrl}/opportunities/workspaces/${workspaceId}/${route}`,
      formData,
    );
  }

  listWorkspaceFiles(
    workspaceId: string,
    uploadTarget: 'CustomerDocuments' | 'InternalDocuments' = 'CustomerDocuments',
  ) {
    return this.http
      .get<WorkspaceFilePublicDto[] | null>(
        `${this.env.apiBaseUrl}/opportunities/workspaces/${workspaceId}/files`,
        {
          params: { uploadTarget },
        },
      )
      .pipe(map((files) => files ?? []));
  }

  getWorkspaceFile(workspaceId: string, fileId: string) {
    return this.http.get<WorkspaceFilePublicDto>(
      `${this.env.apiBaseUrl}/opportunities/workspaces/${workspaceId}/files/${fileId}`,
    );
  }

  getWorkspaceFilesIngestionStatus(workspaceId: string) {
    return this.http.get<WorkspaceFilesIngestionStatusResponseDto>(
      `${this.env.apiBaseUrl}/opportunities/workspaces/${workspaceId}/files/ingestion-status`,
    );
  }

  deleteWorkspaceFile(workspaceId: string, fileId: string) {
    return this.http.delete<void>(
      `${this.env.apiBaseUrl}/opportunities/workspaces/${workspaceId}/files/${fileId}`,
    );
  }
}
